import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { searchAPI, userAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Notification from '../../utils/notification';
import PurchaseConfirmDialog from '../../components/PurchaseConfirmDialog/PurchaseConfirmDialog';
import styles from './ResourceDetail.module.css';

const ResourceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, updateUserBalance } = useAuth();

  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLink, setShowLink] = useState(false);
  const [downloadLink, setDownloadLink] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [latestResources, setLatestResources] = useState([]);

  const fetchResourceDetail = useCallback(async () => {
    try {
      const response = await searchAPI.getResource(id);
      setResource(response.data.data);
    } catch (err) {
      setError('获取资源详情失败');
      console.error('Failed to fetch resource detail:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchLatestResources = useCallback(async () => {
    try {
      const response = await searchAPI.getLatestResources(21);
      const data = response.data.data || {};
      setLatestResources(data.records || []);
    } catch (err) {
      console.error('Failed to fetch latest resources:', err);
    }
  }, []);

  const fetchUserProfile = useCallback(async () => {
    try {
      console.log('Fetching user profile...');
      const response = await userAPI.getProfile();
      console.log('User profile response:', response);
      setUserProfile(response.data.data);
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
      console.error('Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });

      // 临时解决方案：如果获取用户资料失败，创建一个默认资料
      console.log('Creating default user profile...');
      setUserProfile({
        id: 'temp-user',
        username: '用户',
        email: 'user@example.com',
        coinBalance: 1000, // 给用户足够的金币用于测试
        nickname: '测试用户'
      });
    }
  }, []);

  const fetchDownloadLink = async () => {
    try {
      const response = await userAPI.getDownloadLink(id);
      if (response.data?.success || response.data?.code === 200) {
        setDownloadLink(response.data.data);
        setShowLink(true);
      } else {
        throw new Error(response.data?.message || '获取下载链接失败');
      }
    } catch (err) {
      console.error('Failed to fetch download link:', err);
      Notification.error('获取下载链接失败: ' + (err.response?.data?.message || err.message));
    }
  };

  useEffect(() => {
    console.log('useEffect called with:', { id, isAuthenticated: isAuthenticated() });
    // 页面加载时滚动到顶部
    window.scrollTo(0, 0);
    fetchResourceDetail();
    fetchLatestResources();
    if (isAuthenticated()) {
      console.log('User is authenticated, fetching profile...');
      fetchUserProfile();
    } else {
      console.log('User is not authenticated');
    }
  }, [id, isAuthenticated, fetchResourceDetail, fetchLatestResources, fetchUserProfile]);

  const handlePurchase = async () => {
    console.log('handlePurchase called', { resource, userProfile });

    if (!resource || !userProfile) {
      console.error('Missing resource or userProfile', { resource: !!resource, userProfile: !!userProfile });
      Notification.error('数据未加载完成，请稍后再试');
      return;
    }

    setPurchasing(true);
    try {
      console.log('Calling purchaseResource API with id:', id);
      const response = await userAPI.purchaseResource(id);
      console.log('Purchase API response:', response);

      // 更新资源购买状态
      setResource(prev => ({
        ...prev,
        purchased: true
      }));

      // 更新本地用户资料
      const newBalance = userProfile.coinBalance - resource.price;
      setUserProfile(prev => ({
        ...prev,
        coinBalance: newBalance
      }));

      // 更新全局用户金币余额
      if (updateUserBalance) {
        updateUserBalance(newBalance);
      }

      Notification.success('购买成功！');
      setShowPurchaseDialog(false);

      // 购买成功后获取下载链接
      await fetchDownloadLink();
    } catch (err) {
      console.error('Purchase failed with error:', err);
      const errorMessage = err.response?.data?.message || err.message || '请稍后重试';
      Notification.error('购买失败: ' + errorMessage);
    } finally {
      setPurchasing(false);
    }
  };

  const handleShowLink = () => {
    console.log('handleShowLink called', {
      isAuthenticated: isAuthenticated(),
      resource,
      resourcePrice: resource?.price,
      resourcePurchased: resource?.purchased
    });

    if (!isAuthenticated()) {
      navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`);
      return;
    }

    // 检查资源是否需要购买
    if (resource.price > 0 && !resource.purchased) {
      // 检查用户余额
      if (userProfile.coinBalance < resource.price) {
        Notification.error(`金币余额不足！当前余额: ${userProfile.coinBalance}，需要: ${resource.price}`);
        navigate('/checkin');
        return;
      }

      console.log('Resource needs purchase, showing confirmation dialog');
      setShowPurchaseDialog(true);
      return;
    }

    console.log('Fetching download link directly');
    fetchDownloadLink();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      Notification.success('链接已复制到剪贴板');
    }).catch(() => {
      Notification.error('复制失败，请手动复制');
    });
  };

  if (loading) {
    return (
      <div className={styles.resourceDetail}>
        <div className="container">
          <div className={styles.loading}>
            <div className={styles.loadingSpinner}></div>
            <span>加载中...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !resource) {
    return (
      <div className={styles.resourceDetail}>
        <div className="container">
          <div className={styles.error}>
            <h2>资源不存在</h2>
            <p>{error || '请检查链接是否正确'}</p>
            <button
              onClick={() => navigate(-1)}
              className="btn btn-primary"
            >
              返回
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{resource.title} - 资源详情 | 万盘搜</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content={`${resource.description || resource.title} - 文件大小: ${resource.fileSize || '未知'}，来源: ${resource.source || '未知'}。在万盘搜下载网盘资源。`} />
        <meta name="keywords" content={`${resource.title},${resource.tags?.join(',') || ''},网盘资源,网盘下载,${resource.source || ''}`} />
        <link rel="canonical" href={`https://wpansou.aicosmos.cn/resource/${id}`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DigitalDocument",
            "name": resource.title,
            "description": resource.description || resource.title,
            "url": `https://wpansou.aicosmos.cn/resource/${id}`,
            "datePublished": resource.createdAt,
            "fileSize": resource.fileSize,
            "keywords": resource.tags?.join(', '),
            "offers": resource.price > 0 ? {
              "@type": "Offer",
              "price": resource.price,
              "priceCurrency": "COIN"
            } : undefined
          })}
        </script>
      </Helmet>
      <div className={styles.resourceDetail}>
      <div className="container">
        <div className={styles.detailWrapper}>
          {/* 返回按钮 */}
          <div className={styles.detailHeader}>
            <button
              onClick={() => navigate(-1)}
              className={styles.backButton}
            >
              ← 返回
            </button>
          </div>

          {/* 资源详情卡片 */}
          <div className={styles.detailCard}>
            <div className={styles.detailTitleSection}>
              <h1 className={styles.detailTitle}>{resource.title}</h1>
              <div className={styles.detailStatus}>
                <span className={`${styles.statusBadge} ${resource.isValid ? styles.valid : styles.invalid}`}>
                  {resource.isValid ? '有效' : '失效'}
                </span>
              </div>
            </div>

            <div className={styles.detailDescription}>
              <h3>资源描述</h3>
              <p>{resource.description}</p>
            </div>

            <div className={styles.detailMeta}>
              <div className={styles.metaGrid}>
                <div className={styles.metaItem}>
                  <label>网盘来源</label>
                  <span
                    className={styles.sourceBadge}
                  >
                    {resource.source}
                  </span>
                </div>
                <div className={styles.metaItem}>
                  <label>发布日期</label>
                  <span>{formatDate(resource.date)}</span>
                </div>
                <div className={styles.metaItem}>
                  <label>文件大小</label>
                  <span>{resource.fileSize || '未知'}</span>
                </div>
                <div className={styles.metaItem}>
                  <label>下载次数</label>
                  <span>{resource.downloadCount || 0}</span>
                </div>
                {resource.price > 0 && !(resource.downloadUrl || showLink) && (
                  <div className={styles.metaItem}>
                    <label>资源价格</label>
                    <span className={styles.priceText}>{resource.price} 金币</span>
                  </div>
                )}
              </div>
            </div>

            {resource.tags && resource.tags.length > 0 && (
              <div className={styles.detailTags}>
                <h3>相关标签</h3>
                <div className={styles.tagsList}>
                  {resource.tags.map((tag, index) => (
                    <span key={index} className={styles.tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className={styles.detailLinkSection}>
              <h3>网盘链接</h3>
              {(resource.downloadUrl || showLink) ? (
                <div className={styles.linkRevealed}>
                  <div className={styles.linkBox}>
                    <div className={styles.linkInfo}>
                      <div className={styles.linkUrl}>
                        <label>下载链接:</label>
                        <span>{downloadLink?.downloadUrl || resource.downloadUrl || '暂无链接'}</span>
                      </div>
                      {(downloadLink?.extractCode || resource.extractCode) && (
                        <div className={styles.linkCode}>
                          <label>提取码:</label>
                          <span className={styles.extractCode}>
                            {downloadLink?.extractCode || resource.extractCode}
                          </span>
                        </div>
                      )}
                    </div>
                    {(downloadLink?.downloadUrl || resource.downloadUrl) && (
                      <div className={styles.linkActions}>
                        <button
                          onClick={() => copyToClipboard(downloadLink?.downloadUrl || resource.downloadUrl)}
                          className="btn btn-secondary"
                        >
                          复制链接
                        </button>
                        <a
                          href={downloadLink?.downloadUrl || resource.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary"
                        >
                          前往下载
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              ) : !isAuthenticated() ? (
                <div className={styles.loginPrompt}>
                  <p>为保护本站资源，请登录后查看网盘链接</p>
                  <button
                    onClick={() => navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`)}
                    className="btn btn-primary"
                  >
                    立即登录
                  </button>
                </div>
              ) : (
                <div className={styles.linkHidden}>
                  {resource.price > 0 && !resource.purchased ? (
                    <div className={styles.purchasePrompt}>
                      <p>此资源需要购买后才能查看网盘链接（当前余额：{userProfile?.coinBalance || 0} 金币）</p>
                      {userProfile && userProfile.coinBalance < resource.price ? (
                        <div className={styles.insufficientFunds}>
                          <p className={styles.errorText}>金币余额不足，请先签到获取金币</p>
                          <button
                            onClick={() => navigate('/checkin')}
                            className="btn btn-warning"
                          >
                            前往签到
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={handleShowLink}
                          className="btn btn-primary"
                          disabled={purchasing}
                        >
                          {purchasing ? '购买中...' : `购买并显示链接 (${resource.price} 金币)`}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className={styles.freeResource}>
                      <p>点击下方按钮显示网盘链接</p>
                      <button
                        onClick={handleShowLink}
                        className="btn btn-primary"
                      >
                        显示链接
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 推荐服务卡片 */}
          <div className={styles.promoSection}>
            <div className={styles.promoCard}>
              <div className={styles.promoIcon}>🎁</div>
              <div className={styles.promoContent}>
                <h3 className={styles.promoTitle}>注册即送20金币</h3>
                <p className={styles.promoDescription}>免费领取金币</p>
              </div>
              <Link to={`/register?redirect=${encodeURIComponent(location.pathname)}`} className={styles.promoButton}>
                立即注册
              </Link>
            </div>

            <div className={styles.promoCard}>
              <div className={styles.promoIcon}>🎉</div>
              <div className={styles.promoContent}>
                <h3 className={styles.promoTitle}>邀请好友赚金币</h3>
                <p className={styles.promoDescription}>多邀多得上不封顶</p>
              </div>
              <Link to="/profile" className={styles.promoButton}>
                去邀请
              </Link>
            </div>

            <div className={styles.promoCard}>
              <div className={styles.promoIcon}>📅</div>
              <div className={styles.promoContent}>
                <h3 className={styles.promoTitle}>每日签到得金币</h3>
                <p className={styles.promoDescription}>坚持签到享福利</p>
              </div>
              <Link to="/profile" className={styles.promoButton}>
                立即签到
              </Link>
            </div>
          </div>

          {/* 相关资源推荐 */}
          <div className={styles.latestResourcesSection}>
            <h2 className={styles.sectionTitle}>最新资源推荐</h2>
            <div className={styles.resourcesGrid}>
              {latestResources.map((res) => (
                <Link
                  key={res.id}
                  to={`/resource/${res.id}`}
                  className={styles.resourceCard}
                >
                  <div className={styles.resourceCardHeader}>
                    <h4 className={styles.resourceTitle}>{res.title}</h4>
                    <span className={styles.resourceBadge}>{res.source}</span>
                  </div>
                  <p className={styles.resourceDesc}>{res.description || '暂无描述'}</p>
                  <div className={styles.resourceFooter}>
                    {/* <span className={styles.resourceMeta}>{res.fileSize || '未知'}</span> */}
                    {/* {res.price > 0 && <span className={styles.resourcePrice}>{res.price} 金币</span>} */}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* 购买确认对话框 */}
        {showPurchaseDialog && userProfile && (
          <PurchaseConfirmDialog
            isOpen={showPurchaseDialog}
            onClose={() => setShowPurchaseDialog(false)}
            onConfirm={handlePurchase}
            resource={resource}
            userProfile={userProfile}
            purchasing={purchasing}
          />
        )}
      </div>
    </div>
    </>
  );
};

export default ResourceDetail;